import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { BiChevronDown } from 'react-icons/bi';
import { BsFillCameraVideoFill } from 'react-icons/bs';
import { MdMobileScreenShare } from "react-icons/md";
import IconMeet from '../../assets/SVGs/google-meet.svg';
import { useChat } from '../../contexts/chat';
import { useVideoCall } from '../../contexts/videoCall';
import {
  DropdownContainer,
  DropdownItem,
  DropdownMenu,
  LoadingSpinner,
  WrapperInCall
} from './styled';
import { SelectSchedule } from "../SelectSchedule";

const CALL_OPTIONS = {
  MI4U: 'VIDEOCHAMADA_MI4U',
  MEET: 'VIDEOCHAMADA_MEET',
  BOTUCATU: 'VIDEOCHAMADA_BOTUCATU'
};

const CALL_STATUS = {
  WAITING: 1,
  ACTIVE: 2,
  IN_CALL: 'IN_CALL',
  CLOSE_CALL: 'CLOSE_CALL',
  INVITED: 'INVITED'
};

export function VideoCallControls({ customerService }) {

  const [showDropdown, setShowDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef(null);

  const { functionality, accessLevel, idUser } = useChat();

  const {
    openVideoCall,
    onSetDataCall,
    clearDataCall,
    acceptInvite,
    dataCall,
  } = useVideoCall();

  const [showSchedulesModal, setShowSchedulesModal] = useState(false);
  const [schedulesList, setSchedulesList] = useState([]);

  const handleCloseModal = useCallback(() => {
    setShowSchedulesModal(false);
    if (clearDataCall) clearDataCall();
  }, [clearDataCall]);

  const parsedCall = useMemo(() => {
    if (!customerService?.link) return null;
    try {
      return JSON.parse(customerService?.link);
    } catch (error) {
      console.error('Error parsing link:', error);
      return null;
    }
  }, [customerService?.link]);

  const hasVideoFunctionality = useMemo(() =>
    functionality.includes('video') || functionality.includes('video_meet') || functionality.includes('video_botucatu'),
    [functionality]
  );

  const accessPermissions = useMemo(() => {
    const hasMeetAccess = accessLevel?.includes(CALL_OPTIONS.MEET);
    const hasMi4uAccess = accessLevel?.includes(CALL_OPTIONS.MI4U);
    const hasBotucatuAccess = accessLevel?.includes(CALL_OPTIONS.BOTUCATU);
    const activeCount = [hasMeetAccess, hasMi4uAccess, hasBotucatuAccess].filter(Boolean).length;
    return {
      meet: hasMeetAccess,
      mi4u: hasMi4uAccess,
      botucatu: hasBotucatuAccess,
      hasMultiple: activeCount > 1
    };
  }, [accessLevel]);

  const handleClickOutside = useCallback((event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setShowDropdown(false);
    }
  }, []);

  useEffect(() => {
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown, handleClickOutside]);

  const handleVideoCallClick = useCallback(async (callType = null) => {
    setIsLoading(true);
    setShowDropdown(false);
    try {
      const result = await openVideoCall({
        callOption: callType,
        customer: customerService,
        cd_it_agenda_central: ""
      });

      let agendas = null;
      if (Array.isArray(result?.data)) {
        agendas = result.data;
      } else {
        agendas = result?.data?.agendas || result?.data?.schedules || result?.data?.data;
      }

      if (agendas && Array.isArray(agendas) && agendas.length > 0) {
        setSchedulesList(agendas);
        setShowSchedulesModal(true);
        return;
      } else if (agendas && Array.isArray(agendas) && agendas.length === 0) {
        toast.info("Nenhuma agenda disponível encontrada.", { position: 'top-center' });
        return;
      }

    } catch (error) {
      let errorAgendas = null;
      if (Array.isArray(error?.response?.data)) {
        errorAgendas = error.response.data;
      } else {
        errorAgendas = error?.response?.data?.agendas || error?.response?.data?.schedules || error?.response?.data?.data;
      }

      if (errorAgendas && Array.isArray(errorAgendas) && errorAgendas.length > 0) {
        setSchedulesList(errorAgendas);
        setShowSchedulesModal(true);
      } else if (errorAgendas && Array.isArray(errorAgendas) && errorAgendas.length === 0) {
        toast.info("Nenhuma agenda disponível encontrada.", { position: 'top-center' });
      } else {
        toast.error(error?.response?.data?.message || error?.message, {
          position: 'top-center',
          theme: 'colored'
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [customerService, openVideoCall]);

  const handleScheduleSelect = useCallback(async (schedule) => {
    setShowSchedulesModal(false);
    setIsLoading(true);
    try {
      await openVideoCall({
        callOption: CALL_OPTIONS.BOTUCATU,
        customer: customerService,
        cd_it_agenda_central: schedule.cd_it_agenda_central
      });
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message, {
        position: 'top-center',
        theme: 'colored'
      });
    } finally {
      setIsLoading(false);
    }
  }, [customerService, openVideoCall]);

  const toggleDropdown = useCallback(() => {
    setShowDropdown(prev => !prev);
  }, []);

  const renderVideoCallButton = useCallback(() => {
    if (isLoading) {
      return <LoadingSpinner className="loader" />;
    }

    if (accessPermissions.hasMultiple) {
      return (
        <DropdownContainer ref={dropdownRef} accessLevel={accessLevel}>
          <div
            className="video-call-button"
            onClick={toggleDropdown}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggleDropdown();
              }
            }}
            role="button"
            tabIndex={0}
            aria-label="Selecionar tipo de videochamada"
            aria-expanded={showDropdown}
            aria-haspopup="listbox"
          >
            <BsFillCameraVideoFill className='open--call cursor--pointer' aria-hidden="true" />
            <BiChevronDown className="dropdown-arrow" aria-hidden="true" />
          </div>
          {showDropdown && (
            <DropdownMenu>
              {accessPermissions.mi4u && (
                <DropdownItem accessLevel={accessLevel} onClick={() => handleVideoCallClick(CALL_OPTIONS.MI4U)}>
                  <BsFillCameraVideoFill
                    color={accessLevel?.includes('ACESSIBILIDADE') ? '#000000' : '#1b72d6'}
                    className="call-icon"
                  />
                  <span>MI4U Video</span>
                </DropdownItem>
              )}
              {accessPermissions.meet && (
                <DropdownItem accessLevel={accessLevel} onClick={() => handleVideoCallClick(CALL_OPTIONS.MEET)}>
                  <img
                    src={IconMeet}
                    width={22}
                    height={18}
                    alt="Google Meet"
                  />
                  <span>Google Meet</span>
                </DropdownItem>
              )}
              {accessPermissions.botucatu && (
                <DropdownItem accessLevel={accessLevel} onClick={() => handleVideoCallClick(CALL_OPTIONS.BOTUCATU)}>
                  <MdMobileScreenShare
                    color={'#1b72d6'}
                    className="call-icon"
                  />
                  <span>HC em Casa</span>
                </DropdownItem>
              )}
            </DropdownMenu>
          )}
        </DropdownContainer>
      );
    }

    if (accessPermissions.meet) {
      if (accessLevel?.includes('ACESSIBILIDADE')) {
        return (
          <DropdownContainer accessLevel={accessLevel} style={{ paddingLeft: 0 }}>
            <div
              className="video-call-button"
              onClick={() => handleVideoCallClick(CALL_OPTIONS.MEET)}
              role="button"
              tabIndex={0}
              aria-label="Iniciar Google Meet"
            >
              <img
                src={IconMeet}
                width={30}
                height={28}
                alt="Google Meet"
              />
            </div>
          </DropdownContainer>
        );
      }
      return (
        <img
          src={IconMeet}
          width={30}
          height={28}
          alt="Google Meet"
          className='cursor--pointer'
          title="Iniciar Google Meet"
          onClick={() => handleVideoCallClick(CALL_OPTIONS.MEET)}
        />
      );
    }

    if (accessPermissions.mi4u) {
      if (accessLevel?.includes('ACESSIBILIDADE')) {
        return (
          <DropdownContainer accessLevel={accessLevel} style={{ paddingLeft: 0 }}>
            <div
              className="video-call-button"
              onClick={() => handleVideoCallClick(CALL_OPTIONS.MI4U)}
              role="button"
              tabIndex={0}
              aria-label="Iniciar MI4U Video"
            >
              <BsFillCameraVideoFill className='open--call cursor--pointer' />
            </div>
          </DropdownContainer>
        );
      }
      return (
        <BsFillCameraVideoFill
          className='open--call cursor--pointer'
          title="Iniciar MI4U Video"
          onClick={() => handleVideoCallClick(CALL_OPTIONS.MI4U)}
        />
      );
    }

    if (accessPermissions.botucatu) {
      if (accessLevel?.includes('ACESSIBILIDADE')) {
        return (
          <DropdownContainer accessLevel={accessLevel} style={{ paddingLeft: 0 }}>
            <div
              className="video-call-button"
              onClick={() => handleVideoCallClick(CALL_OPTIONS.BOTUCATU)}
              role="button"
              tabIndex={0}
              aria-label="Iniciar Video Botucatu"
            >
              <BsFillCameraVideoFill className='open--call cursor--pointer' />
            </div>
          </DropdownContainer>
        );
      }
      return (
        <BsFillCameraVideoFill
          className='open--call cursor--pointer'
          title="Iniciar Video Botucatu"
          onClick={() => handleVideoCallClick(CALL_OPTIONS.BOTUCATU)}
        />
      );
    }
    return null;
  }, [
    isLoading,
    accessPermissions,
    accessLevel,
    showDropdown,
    toggleDropdown,
    handleVideoCallClick
  ]);


  if (!hasVideoFunctionality) {
    console.log('❌ no video functionality');
    return null;
  }

  if (!accessLevel?.includes("ADMINISTRADOR") && customerService?.usuario_id.toString() !== idUser.toString()) {
    console.log('❌ no admin or user');
    return null
  }

  if ((dataCall?.call?.status === CALL_STATUS.IN_CALL) &&
    (dataCall?.customer?.id === customerService?.id)) return (
      <WrapperInCall variant={1} disabled={true} accessLevel={accessLevel}>
        <div className="call-icon-wrapper">
          <BsFillCameraVideoFill className="call-icon" />
        </div>
        <span className="call-text">Em chamada...</span>
      </WrapperInCall>
    )

  if ((parsedCall?.status === CALL_STATUS.IN_CALL)) {
    return (
      <WrapperInCall variant={1} onClick={() => onSetDataCall(customerService)} accessLevel={accessLevel}>
        <div className="call-icon-wrapper">
          <BsFillCameraVideoFill className="call-icon" />
        </div>
        <span className="call-text">Retornar</span>
      </WrapperInCall>
    );
  }

  if ((parsedCall?.status === CALL_STATUS.INVITED)) {
    return (
      <WrapperInCall variant={2} onClick={() => acceptInvite(customerService)} accessLevel={accessLevel}>
        <div className="call-icon-wrapper">
          <BsFillCameraVideoFill className="call-icon" />
        </div>
        <span className="call-text">Ingressar</span>
      </WrapperInCall>
    );
  }

  if ((dataCall?.call?.status === CALL_STATUS.IN_CALL) &&
    (dataCall?.customer?.id !== customerService?.id)) {
    console.log('❌ in call but different customer');
    return null
  }

  if (customerService?.isChatPreview) {
    console.log('❌ customerService?.isChatPreview');
    return null
  }

  const renderSchedulesModal = () => {
    return (
      <SelectSchedule
        schedulesList={schedulesList}
        showSchedulesModal={showSchedulesModal}
        handleCloseModal={handleCloseModal}
        handleScheduleSelect={handleScheduleSelect}
      />
    );
  }

  return (
    <>
      {renderVideoCallButton()}
      {renderSchedulesModal()}
    </>
  );
}
